import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { IS_DEV_AUTH } from "@/auth/AuthProvider";
import { getMsalInstance } from "@/auth/msalSingleton";
import { apiTokenRequest } from "@/auth/msalConfig";
import { getActiveDevEmail } from "@/auth/devUsers";

const baseURL = import.meta.env.VITE_API_URL || "/api";

export const apiClient = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

// Interceptor: em dev, manda X-Dev-User; em prod, Bearer token do Entra.
apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  config.headers = config.headers ?? {};

  if (IS_DEV_AUTH) {
    config.headers["X-Dev-User"] = getActiveDevEmail();
    return config;
  }

  const msalInstance = getMsalInstance();
  if (!msalInstance) return config;
  const account = msalInstance.getActiveAccount();
  if (!account) return config;

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...apiTokenRequest,
      account,
    });
    config.headers.Authorization = `Bearer ${result.accessToken}`;
  } catch (e) {
    // Falha silenciosa: request segue sem header → 401 → redirect via interceptor abaixo
    // eslint-disable-next-line no-console
    console.warn("[api] acquireTokenSilent falhou:", e);
  }
  return config;
});

// Janela mínima entre tentativas de reautenticação, pra não entrar em loop de
// redirect quando o backend insiste no 401 (ex.: token rejeitado por config).
const AUTH_REDIRECT_KEY = "msal:lastAuthRedirect";
const AUTH_REDIRECT_COOLDOWN_MS = 30_000;

// Interceptor: 401 → tenta reautenticar UMA vez (em prod), com cooldown.
// Em dev, só repassa o erro.
apiClient.interceptors.response.use(
  (res) => res,
  (error: AxiosError) => {
    const status = error.response?.status;
    if (status === 401 && !IS_DEV_AUTH) {
      const last = Number(sessionStorage.getItem(AUTH_REDIRECT_KEY) ?? 0);
      const now = Date.now();
      if (now - last > AUTH_REDIRECT_COOLDOWN_MS) {
        // Primeira falha na janela: tenta renovar o token via redirect.
        sessionStorage.setItem(AUTH_REDIRECT_KEY, String(now));
        const msalInstance = getMsalInstance();
        // acquireTokenRedirect (não loginRedirect): queremos token de API,
        // não um novo login completo.
        msalInstance?.acquireTokenRedirect(apiTokenRequest).catch(() => {});
      } else {
        // 401 repetido dentro do cooldown → o token está sendo rejeitado pelo
        // backend (config/audience/issuer). Redirecionar de novo só gera loop.
        // eslint-disable-next-line no-console
        console.error(
          "[api] 401 repetido — token rejeitado pelo backend. Não redirecionando (evita loop). Verifique a config de auth da API."
        );
      }
    }
    return Promise.reject(error);
  }
);

export interface ApiError {
  error: string;
  details?: unknown;
}

export const isApiError = (e: unknown): e is AxiosError<ApiError> =>
  axios.isAxiosError(e);
